import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import api from "/imports/api/client";
import { applyBranding } from "/imports/libs/branding";
import { trackBeforeLeaving, invoiceProperties } from "/imports/libs/analytics";
import { createWidgetTheme } from "/imports/ui/styles/theme";
import CheckoutTelrCard from "/imports/ui/views/CheckoutTelrCard";
import AutoReleaseNotice from "/imports/ui/views/AutoReleaseNotice";

export default function CheckoutTelrView() {
  const { chargeId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const clubId = queryParams.get("clubId");
  const invoiceId = queryParams.get("invoiceId");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [holdExpired, setHoldExpired] = useState(false);

  const theme = useMemo(() => createWidgetTheme({ primaryColor: data?.widgetColor || undefined }), [data?.widgetColor]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getTelrCharge(chargeId);
        if (cancelled) return;
        setData(res);
        applyBranding({ name: res?.saas?.name, logoUrl: res?.saas?.logoUrl });
        if (res?.payment?.status === "PAID") setPaymentComplete(true);
      } catch (err) {
        // The pending charge is hard-deleted once the invoice is settled out-of-band
        // (dashboard "Complete Payment"), so getTelrCharge 404s. Fall back to the
        // invoice — carried as ?invoiceId= on the link — to show the real outcome
        // (paid/voided) instead of a "Charge not found" error.
        if (cancelled) return;
        if (invoiceId) {
          try {
            const conf = await api.getBookingConfirmation(invoiceId);
            if (cancelled) return;
            setData(conf);
            applyBranding({ name: conf?.saas?.name, logoUrl: conf?.saas?.logoUrl });
          } catch (fallbackErr) {
            if (!cancelled) setError(err.message || "Failed to load payment details.");
          }
        } else {
          setError(err.message || "Failed to load payment details.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chargeId, invoiceId, reloadKey]);

  const handlePay = async () => {
    setError(null);
    try {
      const origin = window.location.origin;
      const res = await api.initiateTelrPayment(
        chargeId,
        {
          ...data.payment,
          authorisedUrl: `${origin}/checkout/telr/${chargeId}/done`,
          declinedUrl: `${origin}/checkout/telr/${chargeId}/declined`,
          cancelledUrl: `${origin}/checkout/telr/${chargeId}/cancelled`
        },
        clubId
      );
      if (!res?.order?.url) {
        setError("Unable to start the payment. Please try again or contact the venue.");
        return;
      }
      setRedirecting(true);
      trackBeforeLeaving("payment_started", invoiceProperties(data));
      window.location.href = res.order.url;
    } catch (err) {
      setError(err.message || "Unable to start the payment. Please try again or contact the venue.");
      // The server rejects payment once the invoice is closed out (cancelled/voided/paid).
      // Re-fetch so the page swaps the stale "Pay now" button for the real closed-out pane,
      // and show the spinner meanwhile so the stale error pane doesn't flash before it.
      setLoading(true);
      setReloadKey((k) => k + 1);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // No charge in the response means we loaded via the invoice fallback (the pending
  // charge was deleted on out-of-band settlement) — there's nothing to pay against here.
  const chargeMissing = !!data && !data.charge;
  const isInvoiceVoided = data?.isInvoiceVoided === true || data?.invoice?.status === "VOIDED";
  // A written-off invoice is an unpaid cancellation — same closed-out, no-longer-payable
  // state as a fully cancelled booking, so it shows the same "booking cancelled" message.
  const isBookingCancelled = data?.isBookingCancelled === true || data?.invoice?.status === "WRITEOFF";
  // Invoice settled out-of-band (e.g. dashboard "Complete Payment" via Visa/Cash) —
  // a fresh online charge here would over-collect.
  const invoiceAlreadyPaid = data?.invoice?.status === "PAID" || data?.invoice?.status === "SETTLED";
  // Past the grace a widget booking is released: no new payment page opens, so the client
  // is sent back to book again instead of being offered a "Pay now" that can only fail.
  const isWidgetInvoice = data?.invoice?.source === "widget-v2";
  const isHoldReleased = isWidgetInvoice && (data?.payWindowClosed === true || holdExpired || data?.invoice?.status === "WRITEOFF");
  const isSale = (data?.invoice?.bookishs || []).length === 0;
  const bookAgainSlug = sessionStorage.getItem("widget_slug") || clubId;

  let rightPaneContent;
  if (paymentComplete || invoiceAlreadyPaid) {
    rightPaneContent = (
      <Box textAlign="center">
        <Typography variant="h6" color="success.main" gutterBottom>
          {invoiceAlreadyPaid && !paymentComplete
            ? "This invoice has already been paid"
            : "Thank you! Your payment was received and has been successfully processed."}
        </Typography>
        {invoiceAlreadyPaid && !paymentComplete && (
          <Typography variant="body2" color="text.secondary">
            No further payment is required. Contact the venue if you need a receipt.
          </Typography>
        )}
      </Box>
    );
  } else if (isInvoiceVoided) {
    rightPaneContent = (
      <Box textAlign="center">
        <Typography variant="h6" color="error.main" gutterBottom>
          This invoice has been voided
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Payment is no longer accepted through this link. Please contact the venue for further assistance.
        </Typography>
      </Box>
    );
  } else if (isHoldReleased) {
    rightPaneContent = (
      <Box textAlign="center" width="100%">
        <Typography variant="h6" color="error.main" gutterBottom>
          {isSale ? "Your purchase has been cancelled" : "Your booking has been released"}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={bookAgainSlug ? 2 : 0}>
          {isSale
            ? "The payment wasn't completed in time. You can start the purchase again."
            : "The payment wasn't completed in time, so the slot was released. You can book it again if it's still free."}
        </Typography>
        {bookAgainSlug && (
          <Button variant="contained" fullWidth size="large" onClick={() => navigate(`/${bookAgainSlug}`)} sx={{ py: 1.5 }}>
            {isSale ? "Start again" : "Book again"}
          </Button>
        )}
      </Box>
    );
  } else if (isBookingCancelled) {
    rightPaneContent = (
      <Box textAlign="center">
        <Typography variant="h6" color="error.main" gutterBottom>
          This booking has been cancelled
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Payment is no longer accepted through this link. Please contact the venue to reschedule your booking.
        </Typography>
      </Box>
    );
  } else if (redirecting) {
    rightPaneContent = (
      <Box textAlign="center">
        <Typography variant="h6" gutterBottom>
          Redirecting to Telr payment page...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we securely connect you to the payment gateway.
        </Typography>
      </Box>
    );
  } else if (error) {
    rightPaneContent = (
      <Box textAlign="center" width="100%">
        <Typography variant="h6" color="error.main" gutterBottom>
          Payment could not be started
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {error}
        </Typography>
        <Button variant="contained" onClick={handlePay}>
          Try again
        </Button>
      </Box>
    );
  } else if (chargeMissing) {
    rightPaneContent = (
      <Box textAlign="center">
        <Typography variant="h6" gutterBottom>
          This payment link is no longer active
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please contact the venue if you need assistance.
        </Typography>
      </Box>
    );
  } else {
    // Only widget bookings are auto-released, so the hold notice is shown only when
    // this club opted in AND the invoice originated from the widget (not a partner pay-link).
    const showAutoReleaseNotice = data?.autoRelease?.enabled === true && data?.invoice?.source === "widget-v2";
    rightPaneContent = (
      <Box width="100%" display="flex" flexDirection="column" alignItems="center">
        <Button variant="contained" fullWidth size="large" onClick={handlePay} sx={{ py: 1.5, letterSpacing: 1 }}>
          PAY NOW
        </Button>
        {showAutoReleaseNotice && (
          <AutoReleaseNotice
            createdAt={data.invoice.createdAt}
            graceMinutes={data.autoRelease.graceMinutes}
            isSale={isSale}
            onExpire={() => setHoldExpired(true)}
          />
        )}
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CheckoutTelrCard data={data} rightPaneContent={rightPaneContent} />
    </ThemeProvider>
  );
}
